import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '../mensagemBanco';
import ColunaEquipes, { type Selecao } from './ColunaEquipes';
import ColunaMontagem from './ColunaMontagem';
import TribosDaEquipe from '@/pages/gerenciar/components/TribosDaEquipe';
import type { EquipePlacar } from '../types';

interface Props {
  eventoId: number;
}

export default function MontagemEquipes({ eventoId }: Props) {
  const [carregando, setCarregando] = useState(true);
  const [equipes, setEquipes] = useState<EquipePlacar[]>([]);
  const [nSemEquipe, setNSemEquipe] = useState(0);
  const [selecionado, setSelecionado] = useState<Selecao>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function carregar() {
    setCarregando(true);
    try {
      const [resEquipes, resSem, resFuncoes] = await Promise.all([
        supabase.from('vw_equipes_placar').select('*').eq('evento_id', eventoId).order('ordem'),
        supabase.from('vw_sem_equipe').select('participante_id').eq('evento_id', eventoId),
        supabase.from('equipes_evento').select('id, funcoes').eq('evento_id', eventoId),
      ]);
      if (resEquipes.error) throw resEquipes.error;

      const funcoesPorId = new Map<number, string | null>();
      ((resFuncoes.data as { id: number; funcoes: string | null }[] | null) ?? []).forEach((r) => {
        funcoesPorId.set(Number(r.id), r.funcoes ?? null);
      });

      setEquipes(
        (((resEquipes.data as EquipePlacar[]) || []).map((e) => ({
          ...e,
          funcoes: funcoesPorId.get(Number(e.equipe_id)) ?? null,
        })) as EquipePlacar[]),
      );
      setNSemEquipe(resSem.data ? resSem.data.length : 0);
      setErro(null);
    } catch (e) {
      setErro(mensagemBanco(e));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  async function aoSalvarVagas(equipe: EquipePlacar, vagas: number): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('salvar_equipe', {
        p_evento: eventoId,
        p_nome: equipe.equipe,
        p_vagas: vagas,
        p_id: equipe.equipe_id,
      });
      if (error) throw error;
      await carregar();
      return true;
    } catch (e) {
      setToast(mensagemBanco(e));
      return false;
    }
  }

  async function aoCriarEquipe(nome: string): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('salvar_equipe', {
        p_evento: eventoId,
        p_nome: nome,
      });
      if (error) throw error;
      await carregar();
      return true;
    } catch (e) {
      setToast(mensagemBanco(e));
      return false;
    }
  }

  async function aoCriarPadrao(): Promise<void> {
    try {
      const { error } = await supabase.rpc('criar_equipes_padrao', { p_evento: eventoId });
      if (error) throw error;
      await carregar();
    } catch (e) {
      setToast(mensagemBanco(e));
    }
  }

  return (
    <div>
      <TribosDaEquipe eventoId={eventoId} />

      {erro && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{erro}</span>
          <button
            type="button"
            onClick={carregar}
            className="cursor-pointer whitespace-nowrap font-medium underline"
          >
            Tentar de novo
          </button>
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div
          className={`${
            selecionado ? 'hidden lg:block' : 'block'
          } w-full lg:w-[320px] lg:shrink-0`}
        >
          <ColunaEquipes
            equipes={equipes}
            carregando={carregando}
            nSemEquipe={nSemEquipe}
            selecionado={selecionado}
            aoSelecionar={setSelecionado}
            aoSalvarVagas={aoSalvarVagas}
            aoCriarEquipe={aoCriarEquipe}
            aoCriarPadrao={aoCriarPadrao}
          />
        </div>

        <div className={`${selecionado ? 'block' : 'hidden lg:block'} min-w-0 flex-1`}>
          <button
            type="button"
            onClick={() => setSelecionado(null)}
            className="mb-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900 lg:hidden"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            Voltar às equipes
          </button>
          <ColunaMontagem
            eventoId={eventoId}
            selecionado={selecionado}
            equipes={equipes}
            aoToast={setToast}
            aoQuadroMudou={carregar}
          />
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-md bg-red-600 px-4 py-3 text-sm text-white">
          {toast}
        </div>
      )}
    </div>
  );
}